var __classPrivateFieldSet = (undefined && undefined.__classPrivateFieldSet) || function (receiver, privateMap, value) {
  if (!privateMap.has(receiver)) {
    throw new TypeError("attempted to set private field on non-instance");
  }
  privateMap.set(receiver, value);
  return value;
};
var __classPrivateFieldGet = (undefined && undefined.__classPrivateFieldGet) || function (receiver, privateMap) {
  if (!privateMap.has(receiver)) {
    throw new TypeError("attempted to get private field on non-instance");
  }
  return privateMap.get(receiver);
};
var _data, _observer, _queue, _serverCancel, _serverDelete, _serverRename, _serverUpload;
class FileManager {
  constructor(handlers) {
    _data.set(this, new Map());
    _observer.set(this, new Map());
    _queue.set(this, new Map());
    _serverCancel.set(this, void 0);
    _serverDelete.set(this, void 0);
    _serverRename.set(this, void 0);
    _serverUpload.set(this, void 0);
    this.cancel = async (id) => {
      const queue = __classPrivateFieldGet(this, _queue).get(id);
      if (queue) {
        __classPrivateFieldGet(this, _queue).delete(id);
        this.executeObservers([id]);
      }
      return __classPrivateFieldGet(this, _serverCancel).call(this, id);
    };
    this.delete = async (id, metadata) => {
      const queue = __classPrivateFieldGet(this, _queue).get(id);
      const data = __classPrivateFieldGet(this, _data).get(id);
      if (queue)
        __classPrivateFieldGet(this, _queue).delete(id);
      if (data)
        __classPrivateFieldGet(this, _data).delete(id);
      if (queue || data)
        this.executeObservers([id]);
      return __classPrivateFieldGet(this, _serverDelete).call(this, id, metadata);
    };
    this.rename = async (id, name) => {
      const queue = __classPrivateFieldGet(this, _queue).get(id);
      const data = __classPrivateFieldGet(this, _data).get(id);
      if (queue)
        __classPrivateFieldGet(this, _queue).set(id, Object.assign(Object.assign({}, queue), { file: Object.assign(Object.assign({}, queue.file), { name }) }));
      if (data)
        __classPrivateFieldGet(this, _data).set(id, Object.assign(Object.assign({}, data), { name }));
      if (queue || data)
        this.executeObservers([id]);
      return __classPrivateFieldGet(this, _serverRename).call(this, id, name);
    };
    this.upload = async (file, metadata) => {
      // Create file ref for queue
      const { id, name, type } = file;
      const hash = await file.hash();
      const url = URL.createObjectURL(file.blob);
      const queued = { hash, id, name, type, url };
      __classPrivateFieldGet(this, _queue).set(file.id, { file: queued, progress: 0 });
      this.executeObservers([id]);
      // Perform upload task
      const partial = await __classPrivateFieldGet(this, _serverUpload).call(this, file, metadata);
      // Remove from queue
      const ref = Object.assign(Object.assign({}, queued), partial);
      if (url !== ref.url)
        URL.revokeObjectURL(queued.url);
      __classPrivateFieldGet(this, _queue).set(file.id, { file: ref, progress: 1 });
      this.executeObservers([id]);
      return ref;
    };
    __classPrivateFieldSet(this, _serverCancel, handlers.cancel);
    __classPrivateFieldSet(this, _serverDelete, handlers.delete);
    __classPrivateFieldSet(this, _serverRename, handlers.rename);
    __classPrivateFieldSet(this, _serverUpload, handlers.upload);
  }
  static inMemory(options = {}) {
    return createInMemoryFileManager(Object.assign({ delay: {} }, options));
  }
  get(id) {
    return __classPrivateFieldGet(this, _data).get(id);
  }
  getIds() {
    // const files = [...this.#data.keys(), ...this.#queue.keys()];
    const files = [];
    // Using simple loops for compatibility
    __classPrivateFieldGet(this, _data).forEach((_, key) => files.push(key));
    __classPrivateFieldGet(this, _queue).forEach((_, key) => files.push(key));
    return Array.from(new Set(files));
  }
  setFiles(files) {
    const data = new Map();
    for (const file of files) {
      __classPrivateFieldGet(this, _queue).delete(file.id);
      data.set(file.id, file);
    }
    const ids = this.getIds();
    const changes = ids.filter(id => {
      const prev = __classPrivateFieldGet(this, _data).get(id);
      const next = data.get(id);
      return !this.isEqual(prev, next);
    });
    __classPrivateFieldSet(this, _data, data);
    if (changes.length)
      this.executeObservers(changes);
  }
  setProgress(id, progress) {
    const queue = __classPrivateFieldGet(this, _queue).get(id);
    if (!queue)
      return;
    __classPrivateFieldGet(this, _queue).set(id, Object.assign(Object.assign({}, queue), { progress }));
    this.executeObservers([id]);
  }
  subscribe(observer, filter) {
    __classPrivateFieldGet(this, _observer).set(observer, filter);
    observer(this.getDataObject(filter));
    return () => this.unsubscribe(observer);
  }
  unsubscribe(observer) {
    __classPrivateFieldGet(this, _observer).delete(observer);
  }
  executeObservers(ids) {
    __classPrivateFieldGet(this, _observer).forEach((filter, observer) => {
      if (!filter || ids.some(id => filter.indexOf(id) >= 0))
        observer(this.getDataObject(filter));
    });
  }
  getDataObject(filter) {
    const files = this.getIds();
    return files.reduce((acc, id) => {
      var _a;
      if (filter && filter.indexOf(id) < 0)
        return acc;
      const data = __classPrivateFieldGet(this, _data).get(id);
      const queue = __classPrivateFieldGet(this, _queue).get(id);
      acc[id] = { file: data || (queue === null || queue === void 0 ? void 0 : queue.file), progress: (_a = queue === null || queue === void 0 ? void 0 : queue.progress) !== null && _a !== void 0 ? _a : 1 };
      return acc;
    }, {});
  }
  isEqual(ref1, ref2) {
    // Check existence
    if (ref1 == undefined || ref2 == undefined)
      return ref1 === ref2;
    // Check change in progress
    if (typeof ref1 === 'number' || typeof ref2 === 'number')
      return ref1 === ref2;
    // Check change in file structure
    for (const prop in ref1) {
      if (ref1[prop] !== ref2[prop])
        return false;
    }
    return true;
  }
}
_data = new WeakMap(), _observer = new WeakMap(), _queue = new WeakMap(), _serverCancel = new WeakMap(), _serverDelete = new WeakMap(), _serverRename = new WeakMap(), _serverUpload = new WeakMap();
const createInMemoryFileManager = (options) => {
  const tasks = new Map();
  const files = new Map();
  const cancel = async (id) => {
    const task = tasks.get(id);
    if (!task)
      return;
    window.clearTimeout(task);
    tasks.delete(id);
  };
  const doDelete = async (id) => {
    files.delete(id);
    update();
  };
  const rename = async (id, name) => {
    const file = files.get(id);
    if (!file)
      return;
    files.set(file.id, Object.assign(Object.assign({}, file), { name }));
    update();
  };
  const upload = file => {
    const { min = 2000, max = 5000 } = options.delay;
    return new Promise(res => {
      const delay = Math.random() * (max - min + 1) + min;
      const timer = window.setTimeout(async () => {
        const ref = {
          hash: await file.hash(),
          id: file.id,
          name: file.name,
          type: file.type,
          url: URL.createObjectURL(file.blob)
        };
        tasks.delete(file.id);
        files.set(file.id, ref);
        res(ref);
        update();
      }, delay);
      tasks.set(file.id, timer);
    });
  };
  const update = () => {
    manager.setFiles(Array.from(files.values()));
  };
  const manager = new FileManager({ cancel, delete: doDelete, rename, upload });
  return manager;
};

class ScrollSpy {
  constructor(selector, options = {}) {
    this.current = null;
    this.nodes = [];
    this.offset = 0;
    this.timeout = 0;
    this.handleScroll = () => {
      if (this.timeout)
        window.cancelAnimationFrame(this.timeout);
      this.timeout = window.requestAnimationFrame(() => this.detect());
    };
    this.selector = selector;
    this.options = Object.assign({ class: 'active', scroll: window }, options);
    this.reload();
    window.addEventListener('resize', this.handleScroll);
    this.options.scroll.addEventListener('scroll', this.handleScroll);
  }
  get active() {
    const last = this.nodes[this.nodes.length - 1];
    if (last && this.bottom && this.isVisible(last.section))
      return last;
    for (let i = this.nodes.length - 1; i >= 0; i--)
      if (this.isVisible(this.nodes[i].section))
        return this.nodes[i];
    return null;
  }
  get bottom() {
    const { root: r } = this;
    return r.scrollHeight - window.innerHeight <= r.scrollTop;
  }
  get root() {
    return (document.scrollingElement || document.documentElement);
  }
  destroy() {
    if (this.current)
      this.deactivate(this.current);
    window.removeEventListener('resize', this.handleScroll);
    this.options.scroll.removeEventListener('scroll', this.handleScroll);
  }
  detect() {
    const { active } = this;
    const current = this.current;
    if (current) {
      if (current === active)
        return;
      else
        this.deactivate(current);
    }
    if (active)
      this.activate(active);
    this.current = active;
  }
  reload() {
    const { root: r } = this;
    const elements = r.querySelectorAll(this.selector);
    this.nodes = Array.from(elements).reduce((acc, el) => {
      const hash = ('hash' in el && el.hash) || '';
      const id = decodeURIComponent(hash.slice(1));
      const section = document.getElementById(id);
      return section ? [...acc, { nav: el, section }] : acc;
    }, []);
    this.offset = Math.min(...this.nodes.map(node => node.section.offsetTop));
    this.detect();
  }
  activate(node) {
    node.nav.classList.add(this.options.class);
  }
  deactivate(node) {
    node.nav.classList.remove(this.options.class);
  }
  isVisible(el, bottom) {
    const rect = el.getBoundingClientRect();
    return bottom ? rect.bottom < window.innerHeight : rect.top <= this.offset;
  }
}

const isEmail = (value) => {
  const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(value);
};

class Validator {
  constructor() {
    this.rules = new Map();
  }
  async validate(validation, el, meta = {}) {
    const { value } = el;
    const metadata = Object.assign(Object.assign({}, meta), { el, tag: el.tagName.toLowerCase() });
    const rules = validation
      .replace(/\s/g, '')
      .split('|')
      .map(r => {
      const [rule, ...argPart] = r.split(':');
      const arg = argPart.join(':');
      const args = arg ? arg.split(',') : [];
      return { rule, args };
    });
    const errors = [];
    for (const { rule, args } of rules) {
      const error = await this.validateRule(rule, args, value, metadata);
      if (error)
        errors.push(error);
    }
    return errors;
  }
  async validateRule(rule, args = [], value, meta) {
    const validator = this.rules.get(rule);
    if (!validator)
      throw new Error(`[${meta.tag}] Unknown validation rule: ${rule}`);
    const result = await validator(value, args, meta);
    const valid = typeof result === 'boolean' ? result : result.valid;
    const message = typeof result === 'boolean' ? '' : result.message;
    if (valid)
      return null;
    const ctx = { args, message, meta, rule, value };
    return {
      rule,
      message: this.translator ? await this.translator(ctx) : message,
      meta
    };
  }
}
const validator = new Validator();
validator.rules.set('alpha', async (value, args, meta) => {
  if (typeof value !== 'string') {
    const message = getUnsupportedMessage('alpha', meta);
    throw new Error(message);
  }
  const valid = /^[A-Z]*$/i.test(value);
  const message = 'This field should only contain alphabetic characters [A-Z].';
  return { valid, message };
});
validator.rules.set('alpha-dash', async (value, args, meta) => {
  if (typeof value !== 'string') {
    const message = getUnsupportedMessage('alpha-dash', meta);
    throw new Error(message);
  }
  const valid = /^[0-9A-Z_-]*$/i.test(value);
  const message = 'This field should only contain alphanumeric characters or dashes [0-9A-Z_-].';
  return { valid, message };
});
validator.rules.set('alpha-num', async (value, args, meta) => {
  if (typeof value !== 'string') {
    const message = getUnsupportedMessage('alpha-num', meta);
    throw new Error(message);
  }
  const valid = /^[0-9A-Z]*$/i.test(value);
  const message = 'This field should only contain alphanumeric characters [0-9A-Z].';
  return { valid, message };
});
validator.rules.set('alpha-spaces', async (value, args, meta) => {
  if (typeof value !== 'string') {
    const message = getUnsupportedMessage('alpha-spaces', meta);
    throw new Error(message);
  }
  const valid = /^[A-Z\s]*$/i.test(value);
  const message = 'This field should only contain alphabetic characters or whitespace [A-Zs].';
  return { valid, message };
});
validator.rules.set('between', async (value, args, meta) => {
  if (typeof value !== 'string' && typeof value !== 'number') {
    const message = getUnsupportedMessage('between', meta);
    throw new Error(message);
  }
  const numbers = args.map(str => Number(str)).filter(v => !isNaN(v));
  if (numbers.length !== 2) {
    const err = `[${meta.tag}] The 'between' validation rule requires two numeric argument (e.g. between:5,10).`;
    throw new Error(err);
  }
  const [min, max] = numbers;
  const valid = min <= Number(value) && max >= Number(value);
  const message = `This field should contain a value between ${min} and ${max}.`;
  return { valid, message };
});
validator.rules.set('digits', async (value, args, meta) => {
  if (typeof value !== 'string' && typeof value !== 'number') {
    const message = getUnsupportedMessage('between', meta);
    throw new Error(message);
  }
  const length = Number(args[0]);
  if (isNaN(length)) {
    const err = `[${meta.tag}] The 'digits' validation rule requires a numeric argument (e.g. digits:5).`;
    throw new Error(err);
  }
  const str = '' + value;
  const valid = /^[0-9]*$/.test(str) && str.length === length;
  const message = `This field requires ${length} digits.`;
  return { valid, message };
});
validator.rules.set('email', async (value, args, meta) => {
  if (typeof value !== 'undefined' && typeof value !== 'string') {
    const message = getUnsupportedMessage('email', meta);
    throw new Error(message);
  }
  const valid = !value || isEmail(value);
  const message = 'This is not a valid email.';
  return { valid, message };
});
validator.rules.set('in', async (value, args) => {
  const valid = Array.isArray(value)
    ? value.every(v => args.indexOf(v) >= 0)
    : args.indexOf('' + value) >= 0;
  const message = 'This value is not allowed.';
  return { valid, message };
});
validator.rules.set('is', async (value, args) => {
  const check = args.length > 1 ? args : args[0];
  const valid = Array.isArray(check)
    ? (Array.isArray(value) && !value.length) ||
      JSON.stringify(check) === JSON.stringify(value)
    : check == undefined || check === '' + value;
  const message = `The value should be: "${check}".`;
  return { valid, message };
});
validator.rules.set('max', async (value, args, meta) => {
  if (typeof value === 'boolean') {
    const message = getUnsupportedMessage('max', meta);
    throw new Error(message);
  }
  const { el } = meta;
  const size = getSize(el);
  const max = getRangeArg(args[0], el);
  if (Array.isArray(value)) {
    const message = `This field may not exceed ${max} options.`;
    return { valid: size <= max, message };
  }
  if ('type' in el && el.type === 'date') {
    const maxDate = new Date(max).toISOString().slice(0, 10);
    const message = `This field max not not exceed ${maxDate}.`;
    return { valid: !value || size <= max, message };
  }
  if (('type' in el && el.type === 'number') || typeof value === 'number') {
    const message = `This field may not exceed ${max}.`;
    return { valid: value == undefined || size <= max, message };
  }
  const message = `This field may not exceed ${max} characters.`;
  return { valid: !value || size <= max, message };
});
validator.rules.set('min', async (value, args, meta) => {
  if (typeof value === 'boolean') {
    const message = getUnsupportedMessage('max', meta);
    throw new Error(message);
  }
  const { el } = meta;
  const size = getSize(el);
  const min = getRangeArg(args[0], el);
  if (Array.isArray(value)) {
    const message = `This requires at least ${min} options.`;
    return { valid: size === 0 || size >= min, message };
  }
  if ('type' in el && el.type === 'date') {
    const minDate = new Date(min).toISOString().slice(0, 10);
    const message = `This field must be after ${minDate}.`;
    return { valid: !value || size >= min, message };
  }
  if (('type' in el && el.type === 'number') || typeof value === 'number') {
    const message = `This field should be ${min} or higher.`;
    return { valid: value == undefined || size >= min, message };
  }
  const message = `This field requires at least ${min} characters.`;
  return { valid: !value || size >= min, message };
});
validator.rules.set('numeric', async (value, args, meta) => {
  if (typeof value !== 'string' && typeof value !== 'number') {
    const message = getUnsupportedMessage('numeric', meta);
    throw new Error(message);
  }
  const str = '' + value;
  const valid = /^[0-9]+$/.test(str);
  const message = 'This field should only contain numeric characters [0-9].';
  return { valid, message };
});
validator.rules.set('phone', async (value, args, meta) => {
  if (typeof value !== 'string') {
    const message = getUnsupportedMessage('phone', meta);
    throw new Error(message);
  }
  const regex = /^([\+][0-9]{1,3}([ \.\-])?)?([\(]{1}[0-9]{3}[\)])?([0-9A-Z \.\-]{1,32})((x|ext|extension)?[0-9]{1,4}?)$/;
  const valid = regex.test(value);
  const message = 'This field should contain a phone number';
  return { valid, message };
});
validator.rules.set('postal-code', async (value, args, meta) => {
  if (typeof value !== 'string') {
    const message = getUnsupportedMessage('postal-code', meta);
    throw new Error(message);
  }
  const valid = /^[1-9][0-9]{3}(?!SA|SD|SS)[A-Z]{2}$/.test(value);
  const message = 'This field should contain a Dutch postal code';
  return { valid, message };
});
validator.rules.set('required', async (value) => {
  const valid = Array.isArray(value) ? !!value.length : value === 0 || !!value;
  const message = 'This field is required.';
  return { valid, message };
});
const getUnsupportedMessage = (rule, meta) => {
  return `[${meta.el}] Unsupported validation rule: ${rule}`;
};
const getSize = (el) => {
  if (Array.isArray(el.value))
    return el.value.length;
  if ('type' in el && el.type === 'date')
    return new Date(el.value || 0).getTime();
  if (('type' in el && el.type === 'number') || typeof el.value === 'number')
    return Number(el.value);
  return ('' + el.value).length;
};
const getRangeArg = (arg, el) => {
  if ('type' in el && el.type === 'date')
    return new Date(arg || 0).getTime();
  return arg == undefined ? 0 : Number(arg);
};

export { FileManager as F, ScrollSpy as S, isEmail as i, validator as v };
